import type { InstallationsDataSource, RepositoryDataSource } from '@/data/installations/constants';
import type {
  DatabaseInstallation,
  InstallationRepositories,
  InstallationRepositoriesResponse,
} from '@/types/github';
import type { SamlSsoInfo } from '@/utils/github/user-auth';
import type { QueryClient } from '@tanstack/react-query';

import { SAML_SSO_ANALYTICS_EVENTS } from '@/lib/analytics/events';

export type InstallationsQueryResult = {
  installations: DatabaseInstallation[];
  samlSso?: SamlSsoInfo;
};

type SamlSsoRequiredRepositoriesError = {
  error: 'saml_sso_required';
  samlSso: NonNullable<InstallationRepositoriesResponse['samlSso']>;
};

export class SamlSsoRequiredRepositoriesQueryError extends Error {
  constructor(public readonly samlSso: NonNullable<InstallationRepositoriesResponse['samlSso']>) {
    super('GitHub SAML SSO authorization is required');
    this.name = 'SamlSsoRequiredRepositoriesQueryError';
  }
}

export function isSamlSsoRequiredRepositoriesQueryError(
  error: unknown,
): error is SamlSsoRequiredRepositoriesQueryError {
  return error instanceof SamlSsoRequiredRepositoriesQueryError;
}

/** Whether a settled repositories query shows GitHub still blocking the user's token. */
export function getRepositoriesSamlSsoOutcomeEvent(result: {
  data?: InstallationRepositoriesResponse;
  error: Error | null;
}) {
  if (result.error) {
    return isSamlSsoRequiredRepositoriesQueryError(result.error)
      ? SAML_SSO_ANALYTICS_EVENTS.REFRESH_STILL_BLOCKED
      : SAML_SSO_ANALYTICS_EVENTS.REFRESH_FAILED;
  }

  return result.data?.samlSso?.required
    ? SAML_SSO_ANALYTICS_EVENTS.REFRESH_STILL_BLOCKED
    : SAML_SSO_ANALYTICS_EVENTS.REFRESH_SUCCESS;
}

function isSamlSsoRequiredRepositoriesErrorBody(
  value: unknown,
): value is SamlSsoRequiredRepositoriesError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    value.error === 'saml_sso_required' &&
    'samlSso' in value &&
    typeof value.samlSso === 'object' &&
    value.samlSso !== null &&
    'required' in value.samlSso &&
    value.samlSso.required === true
  );
}

// v3: Response format changed from DatabaseInstallation[] to { installations, samlSso }
export const installationsQueryKey = (source?: InstallationsDataSource) =>
  source ? ['installations-v3', source] : (['installations-v3'] as const);

export const installationRepositoriesQueryKey = (
  installationId: number | null,
  source: RepositoryDataSource,
) => ['installation-repositories', installationId, source] as const;

export const getInstallationsQueryFn = async (
  source: InstallationsDataSource,
  baseUrl?: string,
  cookie?: string,
): Promise<InstallationsQueryResult> => {
  console.log('[Query] getInstallationsQueryFn: Starting fetch request');

  try {
    const url = baseUrl ? `${baseUrl}/api/installations` : `/api/installations`;
    console.log('[Query] getInstallationsQueryFn: Calling endpoint', url);

    const headers: HeadersInit = {};
    if (cookie) {
      headers.cookie = cookie;
    }

    const response = await fetch(`${url}?source=${source || 'github'}`, { headers });

    console.log('[Query] getInstallationsQueryFn: Response received', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Query] getInstallationsQueryFn: Response not OK', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(
        `Failed to fetch installations: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const data = await response.json();
    console.log('[Query] getInstallationsQueryFn: Response data', data);

    const rawInstallations = data.installations ?? data;
    if (!Array.isArray(rawInstallations)) {
      console.error(
        '[Query] getInstallationsQueryFn: Expected array but got',
        typeof rawInstallations,
      );
      throw new Error('Invalid response format: expected array');
    }

    const processedInstallations = rawInstallations.map((installation: DatabaseInstallation) => {
      console.log('[Query] getInstallationsQueryFn: Processing installation', {
        id: installation.id,
        installationId: installation.installationId,
        accountLogin: installation.accountLogin,
      });
      return {
        ...installation,
        installationId: Number(installation.installationId),
      };
    });

    console.log('[Query] getInstallationsQueryFn: Returning processed data', {
      count: processedInstallations.length,
      installationIds: processedInstallations.map((i: DatabaseInstallation) => i.installationId),
      samlSsoDetected: data.samlSso?.detected,
    });

    return {
      installations: processedInstallations,
      samlSso: data.samlSso,
    };
  } catch (error) {
    console.error('[Query] getInstallationsQueryFn: Error fetching installations', error);
    if (error instanceof Error) {
      console.error('[Query] getInstallationsQueryFn: Error details', {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
    }
    throw error;
  }
};

/**
 * Fetch repositories for an installation - can be used on both client and server
 * On the server, pass the base URL for the full URL and cookies for auth
 */
export const getInstallationRepositoriesQueryFn = async (
  installationId: number | null,
  source: RepositoryDataSource,
  baseUrl?: string,
  cookie?: string,
): Promise<InstallationRepositoriesResponse> => {
  if (!installationId) {
    return {
      total_count: 0,
      repositories: [],
      repository_selection: 'none',
    } as InstallationRepositories;
  }

  const url = baseUrl
    ? `${baseUrl}/api/installations/${installationId}/repositories?source=${source}`
    : `/api/installations/${installationId}/repositories?source=${source}`;

  const headers: HeadersInit = {};
  if (cookie) {
    headers.cookie = cookie;
  }

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as unknown;
    if (isSamlSsoRequiredRepositoriesErrorBody(body)) {
      throw new SamlSsoRequiredRepositoriesQueryError(body.samlSso);
    }

    throw Object.assign(new Error('Failed to fetch installation repositories'), {
      status: response.status,
    });
  }

  const data = (await response.json()) as InstallationRepositoriesResponse;
  return data;
};

export async function prefetchRepositories(
  queryClient: QueryClient,
  installationId: number | null,
  source: RepositoryDataSource,
) {
  try {
    await queryClient.prefetchQuery({
      queryKey: installationRepositoriesQueryKey(installationId, source),
      queryFn: () => getInstallationRepositoriesQueryFn(installationId, source),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  } catch (error) {
    console.debug('Failed to prefetch repositories:', error);
  }
}
